import { useState, useEffect, useRef } from "react"
import { useParams, useLocation } from "wouter"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Camera, MessageSquare, CheckCircle, X } from "lucide-react"
import { VideoUploadPanel } from "@/components/VideoUploadPanel"
import { UploadBatchStatus } from "@/components/UploadBatchStatus"
import { runUploadQueue, validateImageFiles, summarizeResults, type FileResult, type QueueProgress } from "@/lib/uploadQueue"

/** Short non-reversible tag of the link token: scopes remembered video sessions without storing the token. */
function tokenTag(token: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < token.length; i++) { h ^= token.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return h.toString(36)
}

export default function UploadPage() {
  const { token } = useParams()
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const [uploadMessage, setUploadMessage] = useState("")
  const [progress, setProgress] = useState<QueueProgress | null>(null)
  const [batchResults, setBatchResults] = useState<FileResult[] | null>(null)
  const cancelRef = useRef<AbortController | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null)
  const [videoUpload, setVideoUpload] = useState(false)
  const [videoBatch, setVideoBatch] = useState(false)

  useEffect(() => {
    if (token) {
      // Validate token by checking if it exists
      fetch(`/api/upload/${token}`, { method: 'GET' })
        .then(async response => {
          setIsValidToken(response.ok)
          // Hotel video (PLAN.md §22): the server says whether this link may upload videos.
          const data = response.ok ? await response.json().catch(() => null) : null
          setVideoUpload(data?.videoUpload === true)
          setVideoBatch(data?.videoBatch === true)
        })
        .catch(() => {
          setIsValidToken(false)
        })
    }
  }, [token])

  const handleDirectUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || isUploading) return
    // Copy first: clearing the input below empties the live FileList.
    const picked = Array.from(fileList)
    const { valid, skipped } = validateImageFiles(picked)

    if (valid.length === 0) {
      setBatchResults(skipped)
      toast({
        title: "Keine passende Datei",
        description: "Bitte wählen Sie Bilddateien bis 10 MB aus.",
        variant: "destructive"
      })
      return
    }

    setBatchResults(null)
    setIsUploading(true)
    const controller = new AbortController()
    cancelRef.current = controller

    const message = uploadMessage.trim()
    const results = await runUploadQueue(valid, {
      signal: controller.signal,
      onProgress: setProgress,
      upload: (file, signal) => {
        const formData = new FormData()
        formData.append('photo', file)
        if (message) formData.append('message', message)
        return fetch(`/api/upload/${token}`, { method: 'POST', body: formData, signal })
      }
    })
    cancelRef.current = null

    const all = [...results, ...skipped]
    const okCount = results.filter(r => r.status === "ok").length
    const allOk = okCount === results.length && skipped.length === 0
    setProgress(null)
    setIsUploading(false)
    setBatchResults(all)

    // One toast per batch, not one per photo.
    if (okCount > 0) {
      toast({
        title: allOk ? "Upload erfolgreich" : "Upload abgeschlossen",
        description: `${summarizeResults(all)}. Ihr Team erstellt daraus Beiträge.`
      })
      setUploadMessage("")
    } else {
      toast({
        title: "Upload-Fehler",
        description: "Die Fotos konnten nicht hochgeladen werden.",
        variant: "destructive"
      })
    }
    // Leave the page only when everything went through; otherwise keep the list of problems visible.
    if (allOk) setTimeout(() => setLocation("/"), 2000)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleDirectUpload(files)
    }
  }

  if (isValidToken === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-blue-900 dark:to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p>Token wird validiert...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-blue-900 dark:to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center mx-auto">
                <X className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Ungültiger Upload-Link</h2>
                <p className="text-muted-foreground">
                  Dieser Upload-Link ist nicht mehr gültig oder wurde bereits verwendet.
                </p>
              </div>
              <Button onClick={() => setLocation("/")} variant="outline">
                Zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-blue-900 dark:to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-xl">
            <Camera className="h-6 w-6" />
            <span>📸 Fotos für LinkedIn-Content hochladen</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Message Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <MessageSquare className="h-4 w-4" />
                <span>Nachricht (optional)</span>
              </label>
              <Textarea
                placeholder="Beschreiben Sie Ihre Fotos oder ergänzen Sie Kontext"
                value={uploadMessage}
                onChange={(e) => setUploadMessage(e.target.value)}
                className="min-h-[100px] resize-none"
                data-testid="textarea-upload-message"
              />
              <p className="text-xs text-muted-foreground">
                Diese Nachricht hilft beim Erstellen des LinkedIn-Contents
              </p>
            </div>

            {/* Upload Zone */}
            <div 
              className="border-2 border-dashed border-primary/30 rounded-lg p-12 text-center transition-colors hover:border-primary/50 hover:bg-primary/5 cursor-pointer"
              onClick={() => document.getElementById('photo-upload')?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              data-testid="upload-zone"
            >
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { handleDirectUpload(e.target.files); e.target.value = '' }}
                data-testid="input-photo-upload"
              />
              <div className="space-y-4">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Camera className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-medium">Fotos auswählen oder hierher ziehen</p>
                  <p className="text-muted-foreground">
                    Mehrere Fotos möglich. Unterstützte Formate: JPG, PNG, GIF (max. 10MB pro Foto)
                  </p>
                </div>
              </div>
            </div>

            {videoUpload && token && (
              <VideoUploadPanel
                basePath={`/api/upload/${token}/video`}
                accessKey={`link:${tokenTag(token)}`}
                message={uploadMessage}
                batchEnabled={videoBatch}
                batchAllowed={async (signal) => {
                  // Several videos at once (PLAN-multivideo.md §35): the switch is read again before every further file.
                  const res = await fetch(`/api/upload/${token}`, { method: "GET", signal })
                  const data = res.ok ? await res.json().catch(() => null) : null
                  return data?.videoBatch === true
                }}
              />
            )}

            <UploadBatchStatus
              progress={progress}
              results={batchResults}
              onCancel={() => cancelRef.current?.abort()}
            />

            <div className="text-center text-sm text-muted-foreground">
              <p>Ihre Fotos werden automatisch zu Beiträgen verarbeitet</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}